
import { useState, useEffect, useRef, useMemo } from 'react';
import { Play, Pause, RotateCcw, Download, Copy, Film } from 'lucide-react';
import {
  interpolateColors,
  generateCSSAnimation,
  generateAnimationFrames,
  type EasingFunction,
  type ColorSpace,
} from '../lib/colorEngine';

interface ColorAnimationPanelProps {
  color1: string;
  color2: string;
  onCopy: (text: string, field: string) => void;
  copiedField: string | null;
}

export function ColorAnimationPanel({
  color1,
  color2,
  onCopy,
  copiedField,
}: ColorAnimationPanelProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(2000); // milliseconds
  const [easing, setEasing] = useState<EasingFunction>('ease-in-out');
  const [colorSpace, setColorSpace] = useState<ColorSpace>('oklch');
  const [currentFrame, setCurrentFrame] = useState(0);

  const [isRecording, setIsRecording] = useState(false);
  
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  // Generate frames when colors or settings change
  const frames = useMemo(() => generateAnimationFrames(color1, color2, duration, 60, {
    colorSpace,
    easing,
  }), [color1, color2, duration, easing, colorSpace]);

  // Animation loop
  const animateRef = useRef<(timestamp: number) => void>(() => {});
  
  useEffect(() => {
    animateRef.current = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const frameIndex = Math.floor(progress * (frames.length - 1));

      setCurrentFrame(frameIndex);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateRef.current!);
      } else {
        // Loop the animation
        startTimeRef.current = null;
        animationRef.current = requestAnimationFrame(animateRef.current!);
      }
    };
  }, [duration, frames.length]);

  // Play/pause controls
  useEffect(() => {
    if (isPlaying) {
      startTimeRef.current = null;
      animationRef.current = requestAnimationFrame(animateRef.current!);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      startTimeRef.current = null;
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const reset = () => {
    setIsPlaying(false);
    setCurrentFrame(0);
    startTimeRef.current = null;
  };

  const exportCSS = () => {
    const css = generateCSSAnimation(color1, color2, 'colorTransition', duration, {
      colorSpace,
      easing,
    });
    onCopy(css, 'css-animation');
  };

  const startRecording = async () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const stream = canvas.captureStream(60); // 60 fps
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: 2500000,
    });

    recordedChunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `color-transition-${Date.now()}.webm`;
      a.click();
      URL.revokeObjectURL(url);
      setIsRecording(false);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setIsRecording(true);
    setIsPlaying(true);

    // Stop recording after one full cycle
    setTimeout(() => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        setIsPlaying(false);
      }
    }, duration);
  };

  // Draw current frame to canvas
  useEffect(() => {
    if (!canvasRef.current || frames.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const currentColor = frames[currentFrame]?.color || color1;
    ctx.fillStyle = currentColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [currentFrame, frames, color1]);

  const currentColor = frames[currentFrame]?.color || color1;
  const progress = frames[currentFrame]?.progress || 0;

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 space-y-6">
      <header>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Color Animation Preview
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Animate smooth transitions between colors with customizable easing and export options
        </p>
      </header>

      {/* Animation Preview */}
      <div className="space-y-4">
        <div className="relative">
          <div
            className="w-full h-64 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 transition-colors"
            style={{ backgroundColor: currentColor, transitionDuration: '0ms' }}
          />
          <canvas
            ref={canvasRef}
            width={800}
            height={600}
            className="hidden"
          />
          <div className="absolute bottom-4 left-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg p-3">
            <div className="flex items-center justify-between text-white text-sm">
              <span className="font-mono">{currentColor}</span>
              <span className="font-semibold">{Math.round(progress * 100)}%</span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-100"
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        {/* Playback Controls */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={reset}
            className="p-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
            title="Reset"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <button
            onClick={togglePlayPause}
            className="p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-lg"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          </button>
          <button
            onClick={startRecording}
            disabled={isRecording}
            className={`p-3 rounded-lg transition-colors ${
              isRecording
                ? 'bg-red-600 text-white cursor-not-allowed'
                : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
            title={isRecording ? 'Recording...' : 'Record Video'}
          >
            <Film className={`w-5 h-5 ${isRecording ? 'animate-pulse' : ''}`} />
          </button>
        </div>
      </div>

      {/* Animation Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Duration */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Duration: {duration}ms
          </label>
          <input
            type="range"
            min="500"
            max="5000"
            step="100"
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>0.5s</span>
            <span>5s</span>
          </div>
        </div>

        {/* Easing Function */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Easing Function
          </label>
          <select
            value={easing}
            onChange={(e) => setEasing(e.target.value as EasingFunction)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="linear">Linear</option>
            <option value="ease-in">Ease In (Quadratic)</option>
            <option value="ease-out">Ease Out (Quadratic)</option>
            <option value="ease-in-out">Ease In-Out (Quadratic)</option>
            <option value="ease-in-cubic">Ease In (Cubic)</option>
            <option value="ease-out-cubic">Ease Out (Cubic)</option>
            <option value="ease-in-out-cubic">Ease In-Out (Cubic)</option>
          </select>
        </div>

        {/* Color Space */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Interpolation Color Space
          </label>
          <select
            value={colorSpace}
            onChange={(e) => setColorSpace(e.target.value as ColorSpace)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="oklch">OKLCH (Recommended)</option>
            <option value="oklab">OKLab</option>
            <option value="lab">CIE Lab</option>
            <option value="lch">CIE LCh</option>
            <option value="srgb">sRGB</option>
            <option value="hsl">HSL</option>
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            OKLCH provides the most perceptually uniform transitions
          </p>
        </div>

        {/* Frame Count Info */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Animation Info
          </label>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Frames:</span>
              <span className="font-mono text-gray-900 dark:text-gray-100">{frames.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">FPS:</span>
              <span className="font-mono text-gray-900 dark:text-gray-100">60</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Current Frame:</span>
              <span className="font-mono text-gray-900 dark:text-gray-100">{currentFrame + 1}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-4">
          Export Options
        </h4>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={exportCSS}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm"
          >
            {copiedField === 'css-animation' ? (
              <>
                <Copy className="w-4 h-4 text-green-300" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Export CSS Animation</span>
              </>
            )}
          </button>
          <button
            onClick={startRecording}
            disabled={isRecording}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-sm ${
              isRecording
                ? 'bg-red-600 text-white cursor-not-allowed'
                : 'bg-gray-600 hover:bg-gray-700 text-white'
            }`}
          >
            <Film className={`w-4 h-4 ${isRecording ? 'animate-pulse' : ''}`} />
            <span>{isRecording ? 'Recording...' : 'Export as Video'}</span>
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
          CSS animation exports as @keyframes. Video exports as WebM format (one full cycle).
        </p>
      </div>

      {/* Color Gradient Preview */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-3">
          Gradient Preview
        </h4>
        <div
          className="w-full h-12 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
          style={{
            background: `linear-gradient(to right, ${interpolateColors(color1, color2, {
              colorSpace,
              steps: 20,
              easing: 'linear',
            }).join(', ')})`,
          }}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Shows all intermediate colors in the transition (linear interpolation)
        </p>
      </div>
    </div>
  );
}
